import { EnvType } from "../services";
import { IApiClient } from "../api-client";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetBasketResponse } from "../Types/get-basket-response";
import { BasketStatus } from "../Types/basket-status";
import { GetBasketQuickviewResponse } from "../Types/get-basket-quickview-response";
import { GetBasketCountersResponse } from "../Types/get-basket-counters-response";
import { AddItemToBasketRequest } from "../Types/add-item-to-basket-request";
import { SetItemsDirectivesRequest } from "../Types/set-items-directives-request";
import { SetItemsOptionsRequest } from "../Types/set-items-options-request";
import { GetBasketItemComputeBundleMakerMetadataRequest } from "../Types/get-basket-item-compute-bundle-maker-metadata-request";
import { GetBasketItemComputeBundleMakerMetadataResponse } from "../Types/get-basket-item-compute-bundle-maker-metadata-response";
import { SaveCurrentBasketRequest } from "../Types/save-current-basket-request";
import { LoadSavedBasketRequest } from "../Types/load-saved-basket-request";
import { ListFavoriteResponse } from "../Types/list-favorite-response";
import { OrderCheckoutRequest } from "../Types/order-checkout-request";
import { CheckoutResponse } from "../Types/checkout-response";
import { GetBasketDeliveryIdentityProfilesResponse } from "../Types/get-basket-delivery-identity-profiles-response";
import { AddItemsToBasketResponse } from "../Types/add-item-to-basket-response";

interface IBasketApiClient {
  GetBasket(): Promise<GetBasketResponse>;
  GetBasketByStep(step: BasketStatus): Promise<GetBasketResponse>;
  ClearBasket(): Promise<GenericResponseStd>;
  GetBasketQuickView(): Promise<GetBasketQuickviewResponse>;
  GetBasketCounters(): Promise<GetBasketCountersResponse>;
  AddPreconfiguredItemDCPReelsToBasket(request: AddItemToBasketRequest): Promise<GenericResponseStd>;
  AddPreconfiguredItemDCPOVToBasket(request: AddItemToBasketRequest): Promise<GenericResponseStd>;
  AddItemToBasket(request: AddItemToBasketRequest): Promise<GenericResponseStd>;
  AddItemsToBasket(requests: AddItemToBasketRequest[]): Promise<GenericResponseStd>;
  RemoveItemFromBasket(itemId: number): Promise<GenericResponseStd>;
  SetItemsDirectives(request: SetItemsDirectivesRequest): Promise<GenericResponseStd>;
  SetItemsOptions(request: SetItemsOptionsRequest): Promise<GenericResponseStd>;
  DuplicateItem(itemId: number): Promise<GenericResponseStd>;
  GetBasketItemComputeBundleMakerMetadata(
    request: GetBasketItemComputeBundleMakerMetadataRequest
  ): Promise<GetBasketItemComputeBundleMakerMetadataResponse>;
  Checkout(request: OrderCheckoutRequest): Promise<CheckoutResponse>;
  SaveCurrentBasket(request: SaveCurrentBasketRequest): Promise<GenericResponseStd>;
  LoadSavedBasket(request: LoadSavedBasketRequest): Promise<GenericResponseStd>;
  ListSavedBaskets(): Promise<ListFavoriteResponse>;
  DeleteSavedBasket(id: number): Promise<GenericResponseStd>;
  GetBasketDeliveryIdentityProfiles(): Promise<GetBasketDeliveryIdentityProfilesResponse>;
}
export default class BasketApiClient implements IBasketApiClient {
  readonly servicePort = 9524;
  readonly serviceBaseUrl = "basket-service/api";

  basketApiClient: IApiClient;

  constructor(basketApiClient: IApiClient, envType: EnvType) {
    this.basketApiClient = basketApiClient;
    if (envType === "local") this.basketApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetBasket(): Promise<GetBasketResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket`);
  }
  async GetPartialBasket(itemId: number): Promise<GetBasketResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket-partial/${itemId}`);
  }
  async GetBasketByStep(step: BasketStatus): Promise<GetBasketResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket/${step}`);
  }
  async ClearBasket(): Promise<GenericResponseStd> {
    return await this.basketApiClient.delete(`${this.serviceBaseUrl}/basket`);
  }
  async GetBasketQuickView(): Promise<GetBasketQuickviewResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket/quickview`);
  }
  async GetBasketCounters(): Promise<GetBasketCountersResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket/counters`);
  }
  async AddPreconfiguredItemDCPReelsToBasket(request: AddItemToBasketRequest): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(
      `${this.serviceBaseUrl}/basket/items/preconfigured/dcp-reel-by-reel`,
      request
    );
  }
  async AddPreconfiguredItemDCPOVToBasket(request: AddItemToBasketRequest): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(
      `${this.serviceBaseUrl}/basket/items/preconfigured/dcp-original-version`,
      request
    );
  }
  async AddItemToBasket(request: AddItemToBasketRequest): Promise<AddItemsToBasketResponse> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/items`, request);
  }
  async AddItemsToBasket(requests: AddItemToBasketRequest[]): Promise<AddItemsToBasketResponse> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/items/multiple`, requests);
  }
  async RemoveItemFromBasket(itemId: number): Promise<GenericResponseStd> {
    return await this.basketApiClient.delete(`${this.serviceBaseUrl}/basket/items/${itemId}`);
  }
  async SetItemsDirectives(request: SetItemsDirectivesRequest): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/items/directives`, request);
  }
  async SetItemsOptions(request: SetItemsOptionsRequest): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/items/options`, request);
  }
  async DuplicateItem(itemId: number): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/items/duplicate/${itemId}`);
  }
  async GetBasketItemComputeBundleMakerMetadata(
    request: GetBasketItemComputeBundleMakerMetadataRequest
  ): Promise<GetBasketItemComputeBundleMakerMetadataResponse> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/items/compute/bundlemaker/metadata`, request);
  }
  async Checkout(request: OrderCheckoutRequest): Promise<CheckoutResponse> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/checkout`, request);
  }
  async SaveCurrentBasket(request: SaveCurrentBasketRequest): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/save`, request);
  }
  async LoadSavedBasket(request: LoadSavedBasketRequest): Promise<GenericResponseStd> {
    return await this.basketApiClient.post(`${this.serviceBaseUrl}/basket/load`, request);
  }
  async ListSavedBaskets(): Promise<ListFavoriteResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket/list`);
  }
  async DeleteSavedBasket(id: number): Promise<GenericResponseStd> {
    return await this.basketApiClient.delete(`${this.serviceBaseUrl}/basket/delete/${id}`);
  }
  async GetBasketDeliveryIdentityProfiles(): Promise<GetBasketDeliveryIdentityProfilesResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket/broadcaster-profiles`);
  }
  async GetItemDeliveryIdentityProfile(itemId: number): Promise<GetBasketDeliveryIdentityProfilesResponse> {
    return await this.basketApiClient.get(`${this.serviceBaseUrl}/basket/broadcaster-profile/${itemId}`);
  }

  //------Utils------
  GetApiUrl(): string {
    return this.basketApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
